import * as React from "react";
import { useState, useMemo, MouseEvent, ChangeEvent, Dispatch, SetStateAction, useEffect } from "react";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import Toolbar from "@mui/material/Toolbar";
import { Button, FormControl, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TablePagination from "@mui/material/TablePagination";
import { getComparator, Order, stableSort } from "../../../utils/Table";
import SyncIcon from "@mui/icons-material/Sync";
import { IAssetsMetadata } from "../../../services/asset/AssetToolingService";

interface HeadCell {
  id: keyof IAssetsMetadata | string;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "subcategory",
    label: "Subcategory",
    padding: "none",
  },
  {
    id: "category",
    label: "Category",
    padding: "none",
  },
  {
    id: "root",
    label: "Root",
    padding: "none",
  },
  {
    id: "model",
    label: "Model",
  },
  {
    id: "lastTexture",
    label: "Last Texture",
  },
  {
    id: "actions",
    label: "Actions",
    align: "center",
  },
];

interface ArtAssetsMetadataTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: keyof IAssetsMetadata | string) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const ArtAssetsMetadataTableHead = ({ order, orderBy, onRequestSort }: ArtAssetsMetadataTableHeadProps) => {
  const createSortHandler = (property: keyof IAssetsMetadata | string) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

interface ArtAssetsMetadataTableToolbarProps {
  subcategories: string[];
  selectedSubcategory: string;
  setSelectedSubcategory: (subcategory: string) => void;
  categories: string[];
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  models: string[];
  selectedModel: string;
  setSelectedModel: (model: string) => void;
}

const ArtAssetsMetadataTableToolbar = ({
  subcategories,
  selectedSubcategory,
  setSelectedSubcategory,
  categories,
  selectedCategory,
  setSelectedCategory,
  models,
  selectedModel,
  setSelectedModel,
}: ArtAssetsMetadataTableToolbarProps) => {
  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
      <FormControl variant="standard" sx={{ mt: 1, mb: 1, mr: 2, width: "220px" }}>
        <InputLabel id="subcategory-selector-label">Subcategory</InputLabel>
        <Select
          labelId="subcategory-selector-label"
          id="subcategory-selector"
          value={selectedSubcategory}
          onChange={(e) => setSelectedSubcategory(e.target.value)}
          label="Subcategory"
        >
          <MenuItem key="show-all" value="">
            Show All
          </MenuItem>
          {subcategories.map((subcat) => (
            <MenuItem key={subcat} value={subcat}>
              {subcat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="standard" sx={{ mt: 1, mb: 1, mr: 2, width: "220px" }}>
        <InputLabel id="subcategory-selector-label">Category</InputLabel>
        <Select
          labelId="category-selector-label"
          id="category-selector"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          label="Category"
        >
          <MenuItem key="show-all" value="">
            Show All
          </MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="standard" sx={{ mt: 1, mb: 1, width: "220px" }}>
        <InputLabel id="subcategory-selector-label">Model</InputLabel>
        <Select
          labelId="model-selector-label"
          id="model-selector"
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          label="Model"
        >
          <MenuItem key="show-all" value="">
            Show All
          </MenuItem>
          {models.map((model) => (
            <MenuItem key={model} value={model}>
              {model}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Toolbar>
  );
};

export interface ArtAssetsMetadataTableProps {
  rows: IAssetsMetadata[];
  selectedItem: IAssetsMetadata | undefined;
  setSelectedItem: Dispatch<SetStateAction<IAssetsMetadata | undefined>>;
  onUpdateMetadataClick: (data: IAssetsMetadata) => void;
}

export const ArtAssetsMetadataTable = ({
  rows,
  selectedItem,
  setSelectedItem,
  onUpdateMetadataClick,
}: ArtAssetsMetadataTableProps) => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof IAssetsMetadata | string>("creationDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [filteredRows, setFilteredRows] = useState<IAssetsMetadata[]>(rows);

  useEffect(() => {
    setFilteredRows(filter(rows, selectedSubcategory, selectedCategory, selectedModel));
  }, [rows, selectedSubcategory, selectedCategory, selectedModel]);

  const subcategories = () => {
    return Array.from(new Set(rows.map((row) => row.Subcategory))).sort();
  };

  const categories = () => {
    return Array.from(new Set(rows.map((row) => row.Category))).sort();
  };

  const models = () => {
    return Array.from(new Set(rows.map((row) => row.Model.toString(10)))).sort();
  };

  const filter = (rows: IAssetsMetadata[], subcategory: string, category: string, model: string) => {
    return rows.filter((row) => {
      return (
        (row.Category === category || category === "") &&
        (row.Subcategory === subcategory || subcategory === "") &&
        (row.Model.toString(10) === model || model === "")
      );
    });
  };

  const requestSortHandler = (_: MouseEvent<unknown>, property: keyof IAssetsMetadata | string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const pageChangeHandler = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rowsPerPageChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(() => {
    return stableSort(filteredRows, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  const isSelected = (row: IAssetsMetadata, selectedItem: IAssetsMetadata | undefined) =>
    selectedItem !== undefined &&
    row.Subcategory === selectedItem.Subcategory &&
    row.Model === selectedItem.Model &&
    row.lastTexture === selectedItem.lastTexture;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <ArtAssetsMetadataTableToolbar
          subcategories={subcategories()}
          selectedSubcategory={selectedSubcategory}
          setSelectedSubcategory={setSelectedSubcategory}
          categories={categories()}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          models={models()}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} size="small">
            <ArtAssetsMetadataTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={requestSortHandler}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `managed-parameters-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    onClick={() => setSelectedItem(row)}
                    tabIndex={-1}
                    key={`${row.Subcategory}-${row.Model}-${row.lastTexture}`}
                    selected={isSelected(row, selectedItem)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="normal" />
                    <TableCell component="th" id={labelId} scope="row" padding="none">
                      {row.Subcategory}
                    </TableCell>
                    <TableCell align="left">{row.Category}</TableCell>
                    <TableCell align="left">{row.Root}</TableCell>
                    <TableCell align="left">{row.Model}</TableCell>
                    <TableCell align="left">{row.lastTexture}</TableCell>
                    <TableCell>
                      <Stack spacing={1} direction="row" justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SyncIcon />}
                          onClick={() => onUpdateMetadataClick(row)}
                        >
                          Update
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={pageChangeHandler}
          onRowsPerPageChange={rowsPerPageChangeHandler}
        />
      </Paper>
    </Box>
  );
};
